import React, { useState, useContext, useMemo, useEffect } from 'react';

import { AdminPage } from 'components/layout';
import { H4 } from 'components/typography';
import { useTranslation } from 'react-i18next';
import styles from './styles.scss';
import { Link } from 'react-router-dom';
import { uniqBy } from 'lodash';
import {
  Button,
  Table,
  Column,
  QueryString,
  getPageSizeFromQueryString,
  ModalBackground,
  NativeFilterTypes,
  FilterType,
} from 'design-system-web';
import {
  ControlMenu,
  FileInput,
  ResizableWindow,
  AddTenantRelationship,
  ErrorMessage,
} from 'components';

import { UserDataContext } from 'lane-shared/contexts';
import useChannelAdminContext from 'hooks/useChannelAdminContext';
import { hasPermissionOrSuperUser } from 'lane-shared/helpers/hasPermission';
import { PERMISSION_KEYS } from 'constants-permissions';
import { toSchema } from 'lane-shared/helpers';
import { useQuery } from '@apollo/client';
import { queryChannelsByRelationship } from 'lane-shared/graphql/channel';
import { useQueryString, usePersistedParams } from 'hooks';
import { shortAddress, imageUrl } from 'lane-shared/helpers/formatters';
import { getDisplayProfileName } from 'lane-shared/helpers/getDisplayName';
import { FileReturnType, FileReturnTypeEnum } from 'helpers/fileReaderResolver';
import history from 'helpers/history';
import { FeatureFlag } from 'constants-flags';
import { useFlag } from 'lane-shared/hooks';
import { getClient } from 'lane-shared/apollo';
import {
  createAutoSetupConfiguration,
  uploadCSVMutation,
} from 'graphql-queries';
import { TENANT_TYPES } from 'constants-channel';
import { Thumbnail } from 'components/general';
import { routes } from 'lane-shared/config';
import { useRemoveTenantRelationship } from './hooks/useRemoveTenantRelationship';
import { Tenant, Floor } from 'graphql-query-contracts';
import { SortDirectionEnum } from 'constants-activate';

type ModeType = 'relatedTo' | 'channel';
const PER_PAGE = 50;
const TABLE_STATE_STORAGE_VARIABLE =
  'relationships.tenant-management-table-state';

type ServiceRequestQueryString = {
  keyword: string;
  total: number;
  company: string;
  category: string;
  status: string;
  issue: string;
  location: string;
  assignee: string;
  created_at: string;
} & QueryString;

type Row = {
  _id: string;
  name: string;
  type: string;
  subscribers: string;
  sf: string;
  address: string;
  isSub: string;
  slug: string;
  logo: string;
  image: string;
  relationshipId: string;
  floors: string;
  suites: string;
};

const DEFAULT_SEARCH_PARAMS = {
  // table params
  page: 0,
  pageSize: PER_PAGE,
  total: 0,
  sortBy: 'name',
  sortDirection: SortDirectionEnum.Asc,
};

const TRANSLATION_KEYS = {
  uploadSuccessToast:
    'web.pages.portal.admin.channel.relationships.propertyChannelRelationships.uploadSuccessToast',
  uploadErrorTitle:
    'web.pages.portal.admin.channel.relationships.propertyChannelRelationships.uploadError.title',
  uploadErrorMessage:
    'web.pages.portal.admin.channel.relationships.propertyChannelRelationships.uploadError.message',
  createNewTenantButton:
    'web.pages.portal.admin.channel.relationships.propertyChannelRelationships.createNewTenantButton',
  csvImportButton:
    'web.pages.portal.admin.channel.relationships.propertyChannelRelationships.csvImportButton',
  uploadCSVButton:
    'web.pages.portal.admin.channel.relationships.propertyChannelRelationships.uploadCSVButton',
  addButton:
    'web.pages.portal.admin.channel.relationships.propertyChannelRelationships.addNewButton',
  emptyTableMessage:
    'web.pages.portal.admin.channel.relationships.newPropertyChannelRelationships.emptyTable.message',
  header:
    'web.pages.portal.admin.channel.relationships.newPropertyChannelRelationships.header',
  tenantName:
    'web.pages.portal.admin.channel.relationships.newPropertyChannelRelationships.table.tenantName',
  address:
    'web.pages.portal.admin.channel.relationships.newPropertyChannelRelationships.table.address',
  type: 'web.pages.portal.admin.channel.relationships.newPropertyChannelRelationships.table.type',
  squareFootage:
    'web.pages.portal.admin.channel.relationships.newPropertyChannelRelationships.table.squareFootage',
  activeMembers:
    'web.pages.portal.admin.channel.relationships.newPropertyChannelRelationships.table.activeMembers',
  isSubtenant:
    'web.pages.portal.admin.channel.relationships.newPropertyChannelRelationships.table.isSubtenant',
  isSubtenantYes:
    'web.pages.portal.admin.channel.relationships.newPropertyChannelRelationships.table.isSubtenant.yes',
  isSubtenantNo:
    'web.pages.portal.admin.channel.relationships.newPropertyChannelRelationships.table.isSubtenant.no',
  sqrt: 'web.pages.portal.admin.channel.relationships.newPropertyChannelRelationships.table.squareFootage.sqft',
  tenantErrorTitle:
    'web.pages.portal.admin.channel.relationships.newPropertyChannelRelationships.table.error.title',
  tenantErrorMessage:
    'web.pages.portal.admin.channel.relationships.newPropertyChannelRelationships.table.error.message',
  viewDetails:
    'web.pages.portal.admin.channel.relationships.newPropertyChannelRelationships.table.actions.viewDetails',
  removeTenant:
    'web.pages.portal.admin.channel.relationships.newPropertyChannelRelationships.table.actions.remove',
  floors:
    'web.pages.portal.admin.channel.relationships.newPropertyChannelRelationships.table.floors',
  suites:
    'web.pages.portal.admin.channel.relationships.newPropertyChannelRelationships.table.suites',
  suitesWithLease:
    'web.pages.portal.admin.channel.relationships.newPropertyChannelRelationships.table.suites.externalLeaseId',
};

export function NewPropertyChannelRelationships() {
  const { user, hasAnyPermission } = useContext(UserDataContext);
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isCsvOpen, setIsCsvOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const showTenantImport = useFlag(
    FeatureFlag.TenantManagementTenantImport,
    false
  );
  const hideButtons = useFlag(FeatureFlag.TenantManagementHideButtons, false);
  const { channel } = useChannelAdminContext();
  const { removeTenant, isLoading: isRemovingTenant } =
    useRemoveTenantRelationship();

  const storedTableState = window.localStorage.getItem(
    TABLE_STATE_STORAGE_VARIABLE
  );
  const initialTableParams = storedTableState
    ? JSON.parse(storedTableState)
    : DEFAULT_SEARCH_PARAMS;

  const [searchParams, setSearchParams] =
    useQueryString<ServiceRequestQueryString>(DEFAULT_SEARCH_PARAMS);

  const userHasViewEditLeasesPermission = () => {
    if (!user || !channel) {
      return false;
    }

    return hasPermissionOrSuperUser(
      user,
      [PERMISSION_KEYS.PERMISSION_ADMIN],
      channel?._id
    );
  };

  function getVariables(mode: ModeType) {
    const pagination = {
      start:
        ((searchParams?.page || 0) as number) *
        getPageSizeFromQueryString(searchParams?.pageSize),
      perPage: getPageSizeFromQueryString(searchParams?.pageSize),
    };
    const channelSearch = {
      ...(searchParams.sortBy
        ? {
            sortBy: {
              key: searchParams.sortBy,
              dir: searchParams.sortDirection,
            },
          }
        : {}),
      ...(searchParams.keyword
        ? {
            search: {
              type: 'like',
              value: searchParams.keyword,
            },
          }
        : {}),
      ...(searchParams.type
        ? {
            type: {
              any: searchParams.type.split(','),
            },
          }
        : {}),
    };
    const idSearch = { _id: channel?._id };

    // Always include leases to get suites/floors data, but only show lease IDs when permission is true
    const includeLeases = true;

    const variables = {
      pagination,
      search: {
        relatedTo: mode === 'relatedTo' ? channelSearch : idSearch,
        channel: mode === 'relatedTo' ? idSearch : channelSearch,
      },
      includeLeases,
    };

    return variables;
  }

  usePersistedParams({
    searchParams,
    initialTableParams,
    tableStorageVariable: TABLE_STATE_STORAGE_VARIABLE,
  });

  const { data, loading, error } = useQuery(queryChannelsByRelationship, {
    variables: getVariables('channel'),
  });

  const isTableLoading = loading || isRemovingTenant;

  if (error) {
    window.Alert.show({
      title: t(TRANSLATION_KEYS.tenantErrorTitle),
      message: t(TRANSLATION_KEYS.tenantErrorMessage),
      error,
    });
  }

  const totalTenantRequests = data?.channelsByRelationship?.pageInfo?.total;
  const relationships = data?.channelsByRelationship?.items;

  useEffect(() => {
    if (totalTenantRequests) {
      setSearchParams({ total: totalTenantRequests });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data?.channelsByRelationship?.pageInfo]);

  const columns: Column<Row>[] = [
    {
      key: 'name',
      header: t(TRANSLATION_KEYS.tenantName),
      renderCell: (name: string, row: any) => {
        const url = routes.channelAdminRelationshipsTenantView
          .replace(':tenantId', row._id)
          .replace(':id', channel?.slug || '');

        return (
          <div className={styles.logoWrapper}>
            <Thumbnail
              src={row.logo || row.image}
              name={name}
              outline={false}
            />
            <Link to={url} data-test="tenantLink">{name}</Link>
          </div>
        );
      },
      disableVisibilityToggle: true,
    },
    {
      key: 'type',
      header: t(TRANSLATION_KEYS.type),
      renderCell: (value: string) => (
        <span data-test="tenantTypeCell">{value}</span>
      ),
    },
    {
      key: 'subscribers',
      disableSorting: true,
      header: t(TRANSLATION_KEYS.activeMembers),
      type: 'text',
    },
    {
      key: 'sf',
      disableSorting: true,
      header: t(TRANSLATION_KEYS.squareFootage),
      type: 'text',
    },
    {
      key: 'address',
      disableSorting: true,
      header: t(TRANSLATION_KEYS.address),
      type: 'text',
    },
    {
      key: 'isSub',
      disableSorting: true,
      header: t(TRANSLATION_KEYS.isSubtenant),
      type: 'text',
    },
    {
      key: 'floors',
      disableSorting: true,
      header: t(TRANSLATION_KEYS.floors),
      type: 'text',
    },
    {
      key: 'suites',
      disableSorting: true,
      header: userHasViewEditLeasesPermission()
        ? t(TRANSLATION_KEYS.suitesWithLease)
        : t(TRANSLATION_KEYS.suites),
      type: 'text',
    },
  ];

  const rowActions = [
    {
      label: t(TRANSLATION_KEYS.viewDetails),
      onClick: (row: Row) => {
        const url = routes.channelAdminRelationshipsTenantView
          .replace(':tenantId', row._id)
          .replace(':id', channel?.slug || '');

        history.push(url);
      },
    },
    {
      label: t(TRANSLATION_KEYS.removeTenant),
      testId: 'removeTenantAction',
      onClick: (row: Row) => {
        removeTenant({
          relationshipId: row.relationshipId,
          tenantName: row.name,
        });
      },
    },
  ];

  const hasPermission = userHasViewEditLeasesPermission();

  const parseTenantRelationships = useMemo(() => {
    return (relationships: any[]) => {
      return relationships?.map((relationship: any) => {
        const suites: string[] = [];
        const floors: Floor[] = [];
        const tenant: Tenant = relationship.channel?.tenant;

        // Always use leases data, but filter and format differently based on permission
        tenant?.leases?.forEach(lease => {
          // Use suite name if available, otherwise fall back to suiteId
          const suiteName = lease?.suite?.name || lease?.suiteId;

          if (!suiteName) {
            return;
          }

          // When user has permission, show all suites with lease IDs if they have them
          if (hasPermission) {
            if (lease?.leaseId) {
              suites.push(`${suiteName} [${lease.leaseId}]`);
            } else {
              suites.push(suiteName);
            }

            // Include floors for all suites when permission is true
            lease?.suite?.floors?.forEach(floor => {
              if (floor) {
                floors.push(floor);
              }
            });
          } else {
            // When user doesn't have permission, only show suites without lease IDs
            // (empty string or falsy leaseId means no lease ID)
            if (!lease?.leaseId) {
              suites.push(suiteName);
              lease?.suite?.floors?.forEach(floor => {
                if (floor) {
                  floors.push(floor);
                }
              });
            }
          }
        });

        const row: Row = {
          name: getDisplayProfileName(relationship.channel),
          type: relationship.channel?.type,
          subscribers: relationship.channel?.stats?.subscribers,
          sf: `${relationship.channel?.stats?.sf} ${t(TRANSLATION_KEYS.sqrt)}`,
          address: shortAddress(relationship.channel.address),
          isSub: relationship.channel?.isSub
            ? t(TRANSLATION_KEYS.isSubtenantYes)
            : t(TRANSLATION_KEYS.isSubtenantNo),
          slug: relationship.channel?.slug,
          _id: relationship.channel?._id,
          logo: imageUrl(relationship.channel?.profile?.logo),
          image: imageUrl(relationship.channel?.profile?.image),
          relationshipId: relationship._id,
          floors:
            uniqBy(floors, 'id')
              .map(floor => floor.name)
              .filter(Boolean)
              .join(', ') || '-',
          suites: suites.filter(Boolean).join(', ') || '-',
        };

        return row;
      });
    };
  }, [t, hasPermission]);

  const tableRows = useMemo(() => {
    return parseTenantRelationships(relationships) || [];
  }, [relationships, parseTenantRelationships]);

  const renderTable = () => {
    const filters: FilterType[] = [
      {
        isPromoted: true,
        key: 'type',
        label: t(TRANSLATION_KEYS.type),
        type: NativeFilterTypes.Multiselect,
        options: Object.values(TENANT_TYPES).map(toSchema),
      },
    ];

    return (
      <Table
        columns={columns}
        data={tableRows}
        hasKeywordFilter
        showColumnVisibility
        totalRows={Number(searchParams.total)}
        filters={filters}
        tableKey="relationship.relationshipRequestsTable"
        emptyMessage={t(TRANSLATION_KEYS.emptyTableMessage)}
        queryStringsEnabled
        isLoading={isTableLoading}
        pagination="server"
        rowActions={rowActions}
      />
    );
  };

  const handleFileUpload = async (
    file: FileReturnType,
    name: string
  ): Promise<void> => {
    setIsLoading(true);

    try {
      const { data } = await getClient().mutate({
        mutation: uploadCSVMutation,
        variables: {
          uploadCSVData: {
            text: file,
            contentType: 'text/csv',
            fileName: name,
          },
        },
      });

      if (data.uploadCSV.fileUrl) {
        await getClient().mutate({
          mutation: createAutoSetupConfiguration,
          variables: {
            autoSetupData: {
              source: 'csv',
              sourceLocations: [data.uploadCSV.fileUrl],
              parentChannelName: channel && channel.name,
              parentChannelId: channel && channel._id,
              tenantsOnly: true,
            },
          },
        });

        window.Toast.show(t(TRANSLATION_KEYS.uploadSuccessToast));
        setIsCsvOpen(false);
      }
    } catch (e) {
      window.Alert.show({
        title: t(TRANSLATION_KEYS.uploadErrorTitle),
        message: t(TRANSLATION_KEYS.uploadErrorMessage),
        error: e,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const canShowAdminButtons = !hideButtons && !!user?.isSuperUser;

  return (
    <AdminPage className={styles.ChannelRelationships}>
      <div className={styles.header}>
        <H4>{t(TRANSLATION_KEYS.header)}</H4>
        <ControlMenu>
          {canShowAdminButtons && (
            <Link to="relationships/new">
              <Button
                onClick={() => setIsOpen(true)}
                loading={isLoading}
                testId="createTenantButton"
                variant="primary"
                size="large"
              >
                {t(TRANSLATION_KEYS.createNewTenantButton)}
              </Button>
            </Link>
          )}
          {showTenantImport &&
            hasAnyPermission(
              [PERMISSION_KEYS.PERMISSION_PROPERTY_CSV_UPLOAD],
              channel?._id
            ) && (
              <ImportCSVButton
                setIsCsvOpen={setIsCsvOpen}
                loading={isLoading}
              />
            )}
          {canShowAdminButtons && (
            <Button
              onClick={() => setIsOpen(true)}
              loading={isLoading}
              testId="addTenantButton"
              variant="secondary"
              size="large"
            >
              {t(TRANSLATION_KEYS.addButton)}
            </Button>
          )}
        </ControlMenu>
      </div>

      {errorMessage && <ErrorMessage error={errorMessage} />}

      {renderTable()}

      <ModalBackground
        className={styles.background}
        isOpen={isCsvOpen}
        onClose={() => setIsCsvOpen(false)}
      >
        <ResizableWindow
          className={styles.window}
          defaultPosition={ResizableWindow.centerPosition()}
          name="csv-tenant-upload"
        >
          <FileInput
            accept="text/csv"
            type={FileReturnTypeEnum.Text}
            onFileSelected={handleFileUpload}
          >
            <Button
              style={{ marginLeft: '2rem', marginBottom: '1rem' }}
              loading={loading}
              testId="uploadCSVButton"
            >
              {t(TRANSLATION_KEYS.uploadCSVButton)}
            </Button>
          </FileInput>
        </ResizableWindow>
      </ModalBackground>
      <ModalBackground
        className={styles.background}
        isOpen={isOpen}
        onClose={() => setIsOpen(false)}
      >
        <ResizableWindow
          className={styles.window}
          defaultPosition={ResizableWindow.centerPosition()}
          name="add-new-teant"
        >
          <AddTenantRelationship
            setIsOpen={setIsOpen}
            setLoading={setIsLoading}
            setErrorMessage={setErrorMessage}
          />
        </ResizableWindow>
      </ModalBackground>
    </AdminPage>
  );
}

const ImportCSVButton = ({
  setIsCsvOpen,
  loading,
}: {
  setIsCsvOpen: (isOpen: boolean) => void;
  loading: boolean;
}) => {
  const { t } = useTranslation();
  const showAddTenantsUI = useFlag(
    FeatureFlag.TenantManagementAddTenantsUI,
    false
  );

  if (showAddTenantsUI) {
    return (
      <Link to="relationships/add-tenants">
        <Button testId="importCSVButton" variant="secondary" size="large">
          {t(TRANSLATION_KEYS.csvImportButton)}
        </Button>
      </Link>
    );
  }

  return (
    <Button
      onClick={() => setIsCsvOpen(true)}
      loading={loading}
      testId="csvImportButton"
      variant="secondary"
      size="large"
    >
      {t(TRANSLATION_KEYS.csvImportButton)}
    </Button>
  );
};
